import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ImagePlus, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { neighborhoods } from "@/data/neighborhoods";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  address: "", neighborhood: "", neighborhoodSlug: "", status: "sale",
  price: "", beds: "", baths: "", sqft: "", image: "", zillow_url: "", featured: false,
};

const inputCls = "h-11 bg-white border-sand focus-visible:ring-gold rounded-none text-sm";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] font-semibold text-[#525D6B] mb-1.5";

export const ListingForm = ({ open, onOpenChange, listing, onSaved }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const token = localStorage.getItem("cv_admin_token");
      const { data } = await axios.post(`${API}/admin/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setForm((prev) => ({ ...prev, image: data.url }));
      toast.success(t.admin.photoUploaded);
    } catch {
      toast.error(t.form.error);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    if (open) setForm(listing ? { ...listing } : EMPTY);
  }, [open, listing]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem("cv_admin_token");
    const payload = {
      ...form,
      price: Number(form.price),
      beds: Number(form.beds),
      baths: Number(form.baths),
      sqft: Number(form.sqft),
    };
    try {
      if (listing?.id) {
        await axios.put(`${API}/admin/listings/${listing.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/listings`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(t.admin.saved);
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error(t.form.error);
    } finally {
      setSaving(false);
    }
  };

  const statusOptions = [
    { value: "sale", label: t.listingsPage.forSale },
    { value: "sold", label: t.listingsPage.sold },
    { value: "lease", label: t.listingsPage.forLease },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="listing-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-lenis-prevent>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">
            {listing ? t.admin.editListing : t.admin.addListing}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div className="sm:col-span-2">
            <label className={labelCls}>{t.admin.address} *</label>
            <Input data-testid="listing-address-input" value={form.address} onChange={set("address")} className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>{t.admin.neighborhood} *</label>
            <Select
              value={form.neighborhoodSlug}
              onValueChange={(slug) => {
                const n = neighborhoods.find((x) => x.slug === slug);
                setForm({ ...form, neighborhoodSlug: slug, neighborhood: n?.name || "" });
              }}
            >
              <SelectTrigger data-testid="listing-neighborhood-select" className={`${inputCls} [&>span]:text-left`}>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {neighborhoods.map((n) => (
                  <SelectItem key={n.slug} value={n.slug} data-testid={`listing-neighborhood-${n.slug}`}>{n.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>{t.admin.status}</label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="listing-status-select" className={`${inputCls} [&>span]:text-left`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((s) => (
                  <SelectItem key={s.value} value={s.value} data-testid={`listing-status-${s.value}`}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>{t.admin.price} *</label>
            <Input data-testid="listing-price-input" type="number" min="0" value={form.price} onChange={set("price")} className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>{t.admin.sqft}</label>
            <Input data-testid="listing-sqft-input" type="number" min="0" value={form.sqft} onChange={set("sqft")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.admin.beds}</label>
            <Input data-testid="listing-beds-input" type="number" min="0" value={form.beds} onChange={set("beds")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.admin.baths}</label>
            <Input data-testid="listing-baths-input" type="number" min="0" value={form.baths} onChange={set("baths")} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>{t.admin.imageUrl} *</label>
            {form.image && (
              <img src={form.image} alt="" data-testid="listing-image-preview" className="mb-3 w-full aspect-video object-cover border border-sand" />
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="listing-file-input" />
            <button
              type="button"
              data-testid="listing-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-gold/60 bg-gold-light/40 hover:bg-gold-light text-ink font-semibold text-sm px-6 py-4 transition-colors disabled:opacity-60"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4 text-gold-dark" />}
              {uploading ? t.admin.uploading : t.admin.uploadPhoto}
            </button>
            <p className="mt-3 mb-1.5 text-[10px] uppercase tracking-[0.2em] text-[#8C96A4]">{t.admin.orPasteUrl}</p>
            <Input data-testid="listing-image-input" value={form.image} onChange={set("image")} className={inputCls} placeholder="https://…" required />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>{t.admin.zillowUrl}</label>
            <Input data-testid="listing-zillow-input" value={form.zillow_url || ""} onChange={set("zillow_url")} className={inputCls} placeholder="https://www.zillow.com/homedetails/..." />
          </div>
          <div className="sm:col-span-2 flex items-center gap-3">
            <Switch
              data-testid="listing-featured-switch"
              checked={form.featured}
              onCheckedChange={(v) => setForm({ ...form, featured: v })}
            />
            <span className="text-sm text-[#525D6B]">{t.admin.featured}</span>
          </div>
          <div className="sm:col-span-2 flex gap-3 pt-2">
            <button
              data-testid="listing-save-btn"
              type="submit"
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark disabled:opacity-60 text-ink font-semibold text-sm tracking-wide px-6 py-3.5 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.admin.save}
            </button>
            <button
              data-testid="listing-cancel-btn"
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-6 py-3.5 border border-sand text-sm font-semibold text-[#525D6B] hover:border-ink hover:text-ink transition-colors"
            >
              {t.admin.cancel}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
